import React, {useEffect, useState, useRef, useMemo} from 'react';
import useServiceUrls from "../../Utils/UseServiceUrls";
import useDeviceProfile from "../../Utils/UseDeviceProfile";
import LoadingSpinner from "../LoadingSpinner";
import {formatDate, getQueryString, getReturnUrl, completeSnippetOrChainAlerts, hasCartIdentifier, mapServiceUrls, onSnippetComplete} from "../../Helpers/Globals";
import {Button} from "@material-ui/core";
import useSubscriberGroups from "../../Utils/UseSubscriberGroups";
import Header from "../Header";
import useCustomer from "../../Utils/UseCustomer";
import useGetCart from "../../Utils/UseGetCart";
import {client} from "../../Utils/ApiClient";
import {mapSignupField} from "../QuickSignUp/QuickSignup";
import FindCustomerModal from "../FindCustomer/FindCustomerModal";
import useSaveCustomer from "../../Utils/UseSaveCustomer";
import useJoinGroup from "../../Utils/UseJoinGroup";
import useLeaveGroup from "../../Utils/UseLeaveGroup";
import useAlert from "../../Utils/UseAlert";
import useAcknowledgeAlert from "../../Utils/UseAcknowledgeAlert";
import {Checkbox, FormControlLabel} from "@mui/material";
import {useQueryClient} from "react-query";


export default function QuickEdit(props) {
    const { hideHeader = false, customerUid: propCustomerUid, onCustomerSaved } = props;
    const [sessionKey, setSessionKey] = useState('');
    const [loginDomain, setLoginDomain] = useState('');
    const [cartUid, setCartUid] = useState('');
    const [cartExternalId, setCartExternalId] = useState('');
    const [cartSourceExternalId, setCartSourceExternalId] = useState('');
    const [returnUrl, setReturnUrl] = useState('')
    const [customerUid, setCustomerUid] = useState(propCustomerUid || '')
    const { serviceUrlsStatus, serviceUrls, serviceUrlsError } = useServiceUrls(loginDomain);
    const {status, deviceProfile, error} = useDeviceProfile(mapServiceUrls(serviceUrls), sessionKey);
    const {status: groupsStatus, subscriberGroups, error: groupsError} = useSubscriberGroups(serviceUrls, sessionKey)
    const { status: cartStatus, cartResponse, error: cartError, refetch } = useGetCart(serviceUrls, sessionKey, cartUid, cartExternalId, cartSourceExternalId);
    const {status: customerStatus, customer, error: customerError} = useCustomer(serviceUrls, sessionKey, customerUid)
    const [selectedGroups, setSelectedGroups] = useState([])
    const [signupState, setSignupState] = useState({})
    const [badPhone, setBadPhone] = useState(false)
    const [signupComplete, setSignupComplete] = useState(false)
    const [consentGiven, setConsentGiven] = useState(false)
    const saveCustomer = useSaveCustomer()
    const joinGroup = useJoinGroup()
    const leaveGroup = useLeaveGroup()
    
    const [noCustomer, setNoCustomer] = useState(false)
    const [alertUid, setAlertUid] = useState('')
    const prevPropCustomerUidRef = useRef(propCustomerUid)
    const prevSaveStatusRef = useRef(saveCustomer.status)
    const cartResponseRef = useRef(cartResponse)
    cartResponseRef.current = cartResponse
    const { status: alertStatus, alert, error: alertError } = useAlert(serviceUrls, sessionKey, alertUid);
    const acknowledgeAlert = useAcknowledgeAlert();
    const queryClient = useQueryClient();

    useEffect(() => {
        setSessionKey(getQueryString(window.location.href, 'bl_sk') ?? getQueryString(window.location.href,'SessionKey') ?? getQueryString(window.location.href, 'sessionKey') ?? '');
        setLoginDomain(getQueryString(window.location.href, 'bl_LoginDomain') ?? getQueryString(window.location.href,'LoginDomain') ?? getQueryString(window.location.href, 'loginDomain') ?? '');
        setReturnUrl(getReturnUrl(window.location.href))
        setCartUid(getQueryString(window.location.href, 'bL_CartUid') ?? getQueryString(window.location.href,'CartUid') ?? getQueryString(window.location.href, 'cartUid') ?? '');
        setCartExternalId(getQueryString(window.location.href, 'bl_CartExternalId') ?? getQueryString(window.location.href,'cartExternalId') ?? '')
        setCartSourceExternalId(getQueryString(window.location.href, 'bl_CartSourceExternalId') ?? getQueryString(window.location.href,'CartSourceExternalId') ?? '')
        setAlertUid(getQueryString(window.location.href, 'bl_alertUid') ?? getQueryString(window.location.href,'alertUid') ?? '')
    }, [window.location.search])
    
    useEffect(() => {
        // If customerUid is provided via props (from QuickLink), use it and clear noCustomer flag
        if (propCustomerUid) {
            setCustomerUid(propCustomerUid);
            setNoCustomer(false);
            return;
        }
        // Otherwise, check cart for customer if cart exists
        if (cartResponse === undefined)
            return
        if (cartResponse?.Cart?.Customer)
            setCustomerUid(cartResponse.Cart.Customer?.Uid)
        else if (cartResponse)
            setNoCustomer(true)
    }, [cartResponse, propCustomerUid])
    
    // Clear form state when customer is cleared from parent (QuickLink)
    useEffect(() => {
        const prevPropCustomerUid = prevPropCustomerUidRef.current;
        // If propCustomerUid transitions from a value to empty/null, clear form state
        // This handles the case where customer is cleared in QuickLink
        if (prevPropCustomerUid && !propCustomerUid) {
            // Customer was cleared from QuickLink, clear form state
            setCustomerUid('');
            setSignupState({});
            setSelectedGroups([]);
        }
        // Update ref for next comparison
        prevPropCustomerUidRef.current = propCustomerUid;
    }, [propCustomerUid])
    
    useEffect(() => {
        if (customerStatus === 'success') {
            if (!customer)
                setNoCustomer(true)
            else
                setSignupState({...customer, BirthDate: formatDate(customer.BirthDate), ...customer.Address, PhoneNumber: customer.Phone1})
        }
    }, [customerStatus])
    
    // Initialize consent based on whether it's required
    useEffect(() => {
        if (deviceProfile?.POSSnippetSettings) {
            const requiresConsent = deviceProfile.POSSnippetSettings.ConfirmSubscriberGroupChanges;
            // If consent is not required, default to true so groups are enabled
            if (!requiresConsent) {
                setConsentGiven(true);
            }
        }
    }, [deviceProfile])

    const readSnippetParams = (href = window.location.href) => ({
        sessionKey: getQueryString(href, 'bl_sk') ?? getQueryString(href, 'SessionKey') ?? getQueryString(href, 'sessionKey') ?? '',
        cartUid: getQueryString(href, 'bL_CartUid') ?? getQueryString(href, 'CartUid') ?? getQueryString(href, 'cartUid') ?? '',
        cartExternalId: getQueryString(href, 'bl_CartExternalId') ?? getQueryString(href, 'cartExternalId') ?? '',
        cartSourceExternalId: getQueryString(href, 'bl_CartSourceExternalId') ?? getQueryString(href, 'CartSourceExternalId') ?? '',
        returnUrl: getReturnUrl(href),
    })

    const resolveCartContext = () => {
        const urlParams = readSnippetParams()
        const cart = cartResponseRef.current?.Cart
        return {
            cartUid: cartUid || urlParams.cartUid || cart?.Uid || '',
            cartExternalId: cartExternalId || urlParams.cartExternalId || cart?.ExternalId || '',
            cartSourceExternalId: cartSourceExternalId || urlParams.cartSourceExternalId || cart?.SourceExternalId || '',
            returnUrl: returnUrl || urlParams.returnUrl || '',
        }
    }

    const refetchCartForAlerts = async () => {
        const { cartUid: resolvedCartUid, cartExternalId: resolvedExternalId, cartSourceExternalId: resolvedSourceExternalId } = resolveCartContext()
        const resolvedSessionKey = sessionKey || readSnippetParams().sessionKey

        if (resolvedSessionKey && serviceUrls) {
            const urls = mapServiceUrls(serviceUrls)
            const uniqueParam = new Date().getTime()
            const cartQuery = `${resolvedCartUid ? `cartUid=${resolvedCartUid}&` : ''}${resolvedExternalId ? `externalId=${resolvedExternalId}&` : ''}${resolvedSourceExternalId ? `sourceExternalId=${resolvedSourceExternalId}&` : ''}`
            try {
                return await client(urls.LoyaltyEngineApiUrl, `api/v4/${resolvedSessionKey}/carts?${cartQuery}_=${uniqueParam}`)
            } catch (error) {
                console.log(error)
            }
        }

        const { data } = await refetch()
        return data
    }

    const acknowledgeCurrentAlert = async () => {
        const ctx = resolveCartContext()
        const resolvedSessionKey = sessionKey || readSnippetParams().sessionKey
        if (alertUid && hasCartIdentifier(ctx.cartUid, ctx.cartExternalId, ctx.cartSourceExternalId) && serviceUrls && resolvedSessionKey) {
            await acknowledgeAlert.mutateAsync({
                urls: serviceUrls,
                accessKey: resolvedSessionKey,
                cartUid: ctx.cartUid,
                cartExternalId: ctx.cartExternalId,
                cartSourceExternalId: ctx.cartSourceExternalId,
                alertUid
            })
        }
    }

    const finishSnippet = async () => {
        const { returnUrl: resolvedReturnUrl } = resolveCartContext()
        await completeSnippetOrChainAlerts({
            refetchCart: refetchCartForAlerts,
            returnUrl: resolvedReturnUrl,
            acknowledgeAlert: acknowledgeCurrentAlert,
        })
    }

    // Each join/leave is a read-modify-write of the whole customer in the Loyalty Engine, so
    // issuing them in parallel loses every membership change but the last one to be saved.
    // Run them one at a time so all of the selected groups stick.
    const applyGroupChanges = async () => {
        const customerGroups = customer?.SubscriberGroupMemberships ?? []
        const newGroups = selectedGroups.filter(g => !customerGroups.some(cg => cg.Group.Uid === g))
        const removeGroups = customerGroups.filter(cg => !selectedGroups.some(g => cg.Group.Uid === g))

        if (!customer?.Uid || (newGroups.length === 0 && removeGroups.length === 0))
            return

        try {
            for (const groupUid of newGroups)
                await joinGroup.mutateAsync({ accessKey: sessionKey, customerUid: customer.Uid, groupUid, urls: serviceUrls })

            for (const cg of removeGroups)
                await leaveGroup.mutateAsync({ accessKey: sessionKey, customerUid: customer.Uid, groupUid: cg.Group.Uid, urls: serviceUrls })
        } catch (error) {
            console.log(error)
        }

        // The save-customer mutation invalidates the customer query before these group calls
        // run, so the cached customer still has the old memberships. Refresh it here or
        // reopening Quick Edit shows the pre-change selection.
        queryClient.invalidateQueries(['customer'])
    }

    const handlePostSave = async () => {
        await applyGroupChanges()

        if (onCustomerSaved) {
            onCustomerSaved()
            return
        }

        await finishSnippet()
    }

    useEffect(() => {
        if (saveCustomer.status === 'success' && prevSaveStatusRef.current !== 'success') {
            handlePostSave()
        }
        prevSaveStatusRef.current = saveCustomer.status
    }, [saveCustomer.status])

    useEffect(() => {
        if (customer) {
            let groups = []
            ;(customer.SubscriberGroupMemberships ?? []).forEach((g) => groups.push(g.Group.Uid))
            setSelectedGroups(groups)
        }
    }, [customer])

    const handleChange = (field, value) => {
        setSignupState({
            ...signupState,
            [field]: value
        })
        if (field === 'MobilePhone' && badPhone)
            setBadPhone(false)
    }
    
    // Check if login domain requires US formatting (greencross and gxpreprod use non-US)
    const isEnUS = useMemo(() => {
        const domainLower = loginDomain.toLowerCase();
        return domainLower !== 'greencross' && domainLower !== 'gxpreprod';
    }, [loginDomain]);

    const getPhoneErrorMessage = () => {
        return isEnUS 
            ? 'Please enter a valid 10 or 11-digit phone number.'
            : 'Please enter a valid 9 or 10-digit phone number.';
    };

    const isValidPhoneNumber = (phone) => {
        if (!phone) return true; // Empty is valid (unless required)
        // Strip all non-numeric characters for validation
        const numbersOnly = phone.replace(/\D/g, '');
        
        // For US domains: allow 10 or 11 digits
        // For non-US domains (greencross/gxpreprod): allow 9 or 10 digits
        if (isEnUS) {
            return numbersOnly.length === 10 || numbersOnly.length === 11;
        } else {
            return numbersOnly.length === 9 || numbersOnly.length === 10;
        }
    }
    
    const mapSnippetFields = (field, index) => {
        const fieldName = field.FieldName === 'LoyaltyCard' ? 'LoyaltyCardNumber' : field.FieldName
        const current = signupState[fieldName] ?? ''
        return mapSignupField(field, index, current, handleChange, loginDomain)
    }
    
    const toggleGroup = (uid) => {
        // Only require consent if the flag is set
        const requiresConsent = deviceProfile?.POSSnippetSettings?.ConfirmSubscriberGroupChanges;
        if (requiresConsent && !consentGiven) {
            return;
        }
        
        if (selectedGroups.includes(uid)) {
            setSelectedGroups(selectedGroups.filter((g) => g !== uid))
            return;
        }
        
        let groups = selectedGroups.slice()
        groups.push(uid)
        setSelectedGroups(groups)
    }
    
    const mapSubscriberGroup = (group, index) => {
        return <div key={index} className={`quicksignup-subscribergroup ${selectedGroups?.includes(group.Uid) ? 'selected' : ''}`} onClick={() => toggleGroup(group.Uid)}>
            {group.Name}
        </div>
    }
    
    const closeFind = () => {
        setNoCustomer(false)
        refetch()
    }
    
    const acknowledgeAlertIfNeeded = () => {
        acknowledgeCurrentAlert().catch(console.log)
    }
    
    const handleClose = async () => {
        if (onCustomerSaved) {
            onCustomerSaved()
            return
        }

        await finishSnippet()
    }
    
    const submit = (event) => {
        event.preventDefault()
        
        // Validate phone numbers if present or required
        if (deviceProfile?.POSSnippetSettings?.QuickEditFields) {
            const mobilePhoneField = deviceProfile.POSSnippetSettings.QuickEditFields.find(f => f.FieldName === 'MobilePhone');
            
            const mobilePhoneValue = signupState.MobilePhone || '';
            
            // Validate MobilePhone if it has a value or is required
            if (mobilePhoneValue && !isValidPhoneNumber(mobilePhoneValue)) {
                setBadPhone(true);
                return;
            }
            if (mobilePhoneField?.IsRequired && !mobilePhoneValue) {
                setBadPhone(true);
                return;
            }
        }
        
        acknowledgeAlertIfNeeded()
        saveCustomer.mutate({
            urls: serviceUrls,
            accessKey: sessionKey,
            customer: signupState,
        })
    }

    return (
        <div className={'container'}>
            {!hideHeader && <Header title={'Quick Edit'}/>}
            {status === 'loading' && <LoadingSpinner />}
            {noCustomer && <FindCustomerModal close={closeFind}/>}
            {deviceProfile &&
                <>
                    <div className={'snippetContainer center'}>
                        {alert && (
                            <div style={{
                                backgroundColor: '#fff3cd',
                                border: '1px solid #ffc107',
                                borderRadius: '4px',
                                padding: '12px 16px',
                                marginBottom: '20px',
                                fontWeight: 'bold',
                                color: '#856404',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                whiteSpace: 'pre-line'
                            }}>
                                {Array.isArray(alert.Message) ? alert.Message.join('\n') : alert.Message}
                            </div>
                        )}
                        <form onSubmit={submit}>
                            {deviceProfile.POSSnippetSettings.QuickEditFields.map(mapSnippetFields)}
                            {badPhone && <p className='error-text'>{getPhoneErrorMessage()}</p>}
                            <br />
                            {subscriberGroups && 
                                <div>
                                                                            {subscriberGroups.length > 0 && 
                                        <>
                                        <p className={'bold center'}>Add Customer To Groups</p>
                                        {deviceProfile.POSSnippetSettings.ConfirmSubscriberGroupChanges && 
                                            <div style={{
                                                marginTop: '15px',
                                                marginBottom: '15px',
                                                padding: '12px',
                                                borderRadius: '4px',
                                                border: '1px solid #ccc',
                                                backgroundColor: consentGiven ? '#619700' : '#f5f5f5',
                                                cursor: 'pointer',
                                                transition: 'background-color 0.2s'
                                            }} onClick={(e) => {
                                                // Toggle when clicking on container padding, but not on child elements
                                                if (e.target === e.currentTarget) {
                                                    setConsentGiven(!consentGiven);
                                                }
                                            }}>
                                                <div onClick={(e) => e.stopPropagation()}>
                                                    <FormControlLabel 
                                                        control={
                                                            <Checkbox 
                                                                checked={consentGiven}
                                                                onChange={(e) => setConsentGiven(e.target.checked)}
                                                                sx={{
                                                                    color: consentGiven ? 'white' : '#619700',
                                                                    '&.Mui-checked': {
                                                                        color: 'white'
                                                                    }
                                                                }}
                                                            />
                                                        } 
                                                        label={deviceProfile.POSSnippetSettings.ConfirmSubscriberGroupChangesMessage ? deviceProfile.POSSnippetSettings.ConfirmSubscriberGroupChangesMessage : "Received customer consent to receive subscriber group related Emails and/or Texts"}
                                                        sx={{
                                                            color: consentGiven ? 'white' : '#333',
                                                            margin: 0,
                                                            userSelect: 'none',
                                                            width: '100%'
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        }
                                        
                                        </>}
                                    <div style={{
                                        marginTop: '25px', 
                                        opacity: (deviceProfile?.POSSnippetSettings?.ConfirmSubscriberGroupChanges && !consentGiven) ? 0.5 : 1, 
                                        pointerEvents: (deviceProfile?.POSSnippetSettings?.ConfirmSubscriberGroupChanges && !consentGiven) ? 'none' : 'auto'
                                    }}>
                                        {subscriberGroups.map(mapSubscriberGroup)}
                                    </div>
                                </div>
                            }
                            <br />
                            <br />
                            <Button style={{margin: '5px'}} variant={'text'} className={'left'} color={'primary'} onClick={handleClose}>Close</Button>
                            <Button style={{margin: '5px'}} type='submit' className={'right'} variant='contained' color={'primary'} disabled={saveCustomer.status === 'loading' || joinGroup.status === 'loading' || leaveGroup.status === 'loading' || customer?.EditAtPOS === false}>Save</Button>
                            {(saveCustomer.status === 'loading' || joinGroup.status === 'loading' || leaveGroup.status === 'loading') && <LoadingSpinner />}
                            {saveCustomer.status === 'error' && <p className='error-text'>{saveCustomer.error}</p>}
                            {leaveGroup.status === 'error' && <p className='error-text'>{leaveGroup.error}</p>}
                            {joinGroup.status === 'error' && <p className='error-text'>{joinGroup.error}</p>}
                            {customerStatus === 'error' && <p className='error-text'>{customerStatus}</p>}
                            {serviceUrlsStatus === 'error' && <p className='error-text'>{serviceUrlsError}</p>}
                        </form>
                    </div>
                </>
            }
        </div>
    )
}